// Library Methods
import { useTranslation } from "react-i18next";
import React from "react";

// Components
import { Box, Divider, Grid, Typography } from "@mui/material";
import ExamCardButton from "../ui/ExamCardButton";
import ExamCardToggles from "../ui/ExamCardToggles";
import TitleWithTooltip from "../../../../../components/UI/TitleWithTooltip";
import ExamCardContainer from "../ui/ExamCardContainer";
import ExamCardHeader from "../ui/ExamCardHeader";
import { positionTypes } from "../../../../../enums";

const SynoptophoreCard = ({
  label,
  examAlgorithm,
  selectedExamCards,
  updateExamCardProperty,
  onToggleCard,
}) => {
  // internationalization
  const { t } = useTranslation();

  const { near, far } = positionTypes;

  const defaultSynoptophore = {
    examFieldType: examAlgorithm,
    examType: "BINOCULAR_VISION",
    stereopsis: true,
    fusion: true,
    eyeCheckboxes: { right: true, left: true },
    position: near,
    binocular: true,
    optionalToggles: {},
  };

  const selectedExamCard = selectedExamCards.find(
    (examCard) => examCard.examFieldType === examAlgorithm
  );
  const examCardInfo = selectedExamCard ?? defaultSynoptophore;
  const isActive = Boolean(selectedExamCard);

  const { stereopsis, fusion, position, optionalToggles } = examCardInfo;

  const textStyle = {
    color: isActive ? "#FFF" : "#5B6D7A",
    fontSize: "14px",
  };

  return (
    <ExamCardContainer isActive={isActive}>
      <ExamCardHeader
        label={label}
        isActive={isActive}
        onChange={(e) => {
          onToggleCard(e, defaultSynoptophore);
        }}
      />
      <Grid item container display="flex" flexDirection="column" gap="8px">
        {/* stereopsis */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("word_stereopsis")}
            tooltip={t("stereopsis_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            <Grid item xs>
              <ExamCardButton
                isSelected={stereopsis}
                updateValue={() => updateExamCardProperty("stereopsis", true)}
                label={t("word_on")}
                isDisabled={!isActive}
              />
            </Grid>
            <Grid item xs>
              <ExamCardButton
                isSelected={!stereopsis}
                updateValue={() => updateExamCardProperty("stereopsis", false)}
                label={t("word_off")}
                isDisabled={!isActive}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* fusion */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("word_fusion")}
            tooltip={t("fusion_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            <Grid item xs>
              <ExamCardButton
                isSelected={fusion}
                updateValue={() => updateExamCardProperty("fusion", true)}
                label={t("word_on")}
                isDisabled={!isActive}
              />
            </Grid>
            <Grid item xs>
              <ExamCardButton
                isSelected={!fusion}
                updateValue={() => updateExamCardProperty("fusion", false)}
                label={t("word_off")}
                isDisabled={!isActive}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* position */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("exams_table_position")}
            tooltip={t("more_available_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            <Grid item xs>
              <ExamCardButton
                isSelected={position === near}
                updateValue={() => updateExamCardProperty("position", near)}
                label={t("word_near")}
                isDisabled={!isActive}
              />
            </Grid>
            <Grid item xs>
              <ExamCardButton
                isSelected={position === far}
                updateValue={() => updateExamCardProperty("position", far)}
                label={t("word_far")}
                isDisabled={!isActive}
              />
            </Grid>
          </Grid>
        </Grid>

        <Box height={{ xs: "80px", sm: "356.5px" }} />

        <Divider color="#5B6D7A" />

        <Grid display="flex" flexDirection="column">
          <ExamCardToggles
            isActive={isActive}
            optionalToggles={optionalToggles}
            updateExamCardProperty={updateExamCardProperty}
            showToggles={defaultSynoptophore.optionalToggles}
          />
        </Grid>

        <Grid item textAlign="center">
          <Typography color={isActive ? "#FFF" : "#5B6D7A"} fontSize="14px">
            {t("new_test")}
          </Typography>
          <Typography color={isActive ? "#FFF" : "#5B6D7A"} fontSize="14px">
            {t("try_it_out")}
          </Typography>
        </Grid>
      </Grid>
    </ExamCardContainer>
  );
};

export default SynoptophoreCard;
